import { Models, Query } from "appwrite";
import { useRef, useCallback, useState, useEffect } from "react";
import { useToast } from "@/components/ui/use-toast";
import { Loader, PostCard, UserCard } from "@/components/shared";
import { useGetRecentPosts, useGetUsers, useUpdatePost } from "@/lib/react-query/queries";
import { getPostById, getUserById } from "@/lib/appwrite/api";
import { databases } from "@/lib/appwrite/config";
import { appwriteConfig } from "@/lib/appwrite/config";
import { 
  Plus, Edit3, Trash2, Save, X, Eye, Clock, Star, 
  BookOpen, Users as Settings 
} from "lucide-react";

type TabType = 'posts' | 'products' | 'scheduled' | 'banned' | 'userguide';

interface Product extends Models.Document {
  title: string;
  price: string;
  description: string;
  images: string[];
  sellerId: string;
  status: string;
  isActivated: boolean;
}

interface ScheduledLive extends Models.Document {
  sellerId: string;
  title: string;
  products: string[];
  scheduledAt: string;
  discountMaxUsers?: number;
  discountPercent?: number;
  status: string;
  isActivated: boolean;
}

interface UserViolation extends Models.Document {
  userId: string;
  violationStage: string;
  violationCount: number;
  messageText: string;
  lastViolationType: string;
  flaggedUntil?: string;
}

interface UserViolationWithDetails extends UserViolation {
  username?: string;
  userEmailOrPhone?: string;
}

// NEW: User Guide Interfaces
interface Article extends Models.Document {
  title: string;
  content: string;
  excerpt: string;
  categoryId: string;
  readTime: number;
  featured: boolean;
  published: boolean;
  createdAt: string;
  updatedAt: string;
  slug: string;
  tags: string[];
  views: number;
  scheduledPublishDate?: string;
}

interface Category extends Models.Document {
  title: string;
  description: string;
  icon: string;
  color: string;
  order: number;
  published: boolean;
  createdAt: string;
  updatedAt: string;
  slug: string;
  articleCount: number;
}

const Admin = () => {
  const { toast } = useToast();
  const [activeTab, setActiveTab] = useState<TabType>('posts');
  const [products, setProducts] = useState<Product[]>([]);
  const [scheduledLives, setScheduledLives] = useState<ScheduledLive[]>([]);
  const [bannedUsers, setBannedUsers] = useState<UserViolationWithDetails[]>([]);
  const [isLoadingProducts, setIsLoadingProducts] = useState(false);
  const [isLoadingScheduled, setIsLoadingScheduled] = useState(false);
  const [isLoadingBanned, setIsLoadingBanned] = useState(false);

  // NEW: User Guide States - MOVED TO TOP LEVEL
  const [articles, setArticles] = useState<Article[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [isLoadingArticles, setIsLoadingArticles] = useState(false);
  const [isLoadingCategories, setIsLoadingCategories] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState<'article' | 'category'>('article');
  const [editingItem, setEditingItem] = useState<any>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [guideActiveTab, setGuideActiveTab] = useState<'articles' | 'categories'>('articles'); // MOVED HERE

  // NEW: Form States
  const [articleForm, setArticleForm] = useState({
    title: '',
    content: '',
    excerpt: '',
    categoryId: '',
    readTime: 5,
    featured: false,
    published: true,
    tags: [] as string[],
    scheduledPublishDate: ''
  });

  const [categoryForm, setCategoryForm] = useState({
    title: '',
    description: '',
    icon: 'BookOpen',
    color: 'bg-blue-500',
    order: 1,
    published: true
  });

  const {
    data,
    isLoading: isPostLoading,
    isError: isErrorPosts,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useGetRecentPosts(undefined, undefined, false);

  const {
    data: creators,
    isLoading: isUserLoading,
    isError: isErrorCreators,
  } = useGetUsers(50);

  const observerRef = useRef<IntersectionObserver | null>(null);

  const lastPostRef = useCallback(
    (node: HTMLLIElement | null) => {
      if (isFetchingNextPage) return;
      if (observerRef.current) observerRef.current.disconnect();
      observerRef.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && hasNextPage) {
          fetchNextPage();
        }
      });
      if (node) observerRef.current.observe(node);
    },
    [isFetchingNextPage, hasNextPage, fetchNextPage]
  );

  // Existing functions - unchanged
  const fetchProducts = async () => {
    setIsLoadingProducts(true);
    try {
      const response = await databases.listDocuments(
        appwriteConfig.databaseId,
        appwriteConfig.products,
        [Query.equal("isActivated", [false])]
      );
      setProducts(response.documents as unknown as Product[]);
    } catch (error) {
      console.error('Error fetching products:', error);
      toast({
        title: "Error",
        description: "Failed to fetch products",
        variant: "destructive",
      });
    } finally {
      setIsLoadingProducts(false);
    }
  };

  const fetchScheduledLives = async () => {
    setIsLoadingScheduled(true);
    try {
      const response = await databases.listDocuments(
        appwriteConfig.databaseId,
        appwriteConfig.scheduledLivesCollectionId,
        [Query.equal("isActivated", [false])]
      ) 
      setScheduledLives(response.documents as unknown as ScheduledLive[]);
    } catch (error) {
      console.error('Error fetching scheduled lives:', error);
      toast({
        title: "Error",
        description: "Failed to fetch scheduled lives",
        variant: "destructive",
      });
    } finally {
      setIsLoadingScheduled(false);
    }
  };

  const fetchBannedUsers = async () => {
    setIsLoadingBanned(true);
    try {
      const response = await databases.listDocuments(
        appwriteConfig.databaseId,
        appwriteConfig.userViolationsCollectionId,
        [Query.equal("violationStage", ["banned"])]
      );

      const violationsWithDetails = await Promise.all(
        response.documents.map(async (doc) => {
          const violation = doc as unknown as UserViolation;

          try {
            const user = await getUserById(violation.userId);
            return {
              ...violation,
              username: user?.name || 'Unknown User',
              userEmailOrPhone: user?.emailOrPhone || 'No email',
            } as UserViolationWithDetails;
          } catch (error) {
            console.error(`Error fetching user ${violation.userId}:`, error);
            return {
              ...violation,
              username: 'Unknown User',
              userEmailOrPhone: 'No email',
            } as UserViolationWithDetails;
          }
        })
      );

      setBannedUsers(violationsWithDetails);
    } catch (error) {
      console.error('Error fetching banned users:', error);
      toast({
        title: 'Error',
        description: 'Failed to fetch banned users',
        variant: 'destructive',
      });
    } finally {
      setIsLoadingBanned(false);
    }
  };

  // NEW: User Guide Functions
  const fetchArticles = async () => {
    setIsLoadingArticles(true);
    try {
      const response = await databases.listDocuments(
        appwriteConfig.databaseId,
        appwriteConfig.userGuideArtice, // Your collection ID
        []
      );
      setArticles(response.documents as unknown as Article[]);
    } catch (error) {
      console.error('Error fetching articles:', error);
      toast({
        title: "Error",
        description: "Failed to fetch articles",
        variant: "destructive",
      });
    } finally {
      setIsLoadingArticles(false);
    }
  };

  const fetchCategories = async () => {
    setIsLoadingCategories(true);
    try {
      const response = await databases.listDocuments(
        appwriteConfig.databaseId,
        appwriteConfig.userGuideCategories, // Your collection ID
        []
      );
      setCategories(response.documents as unknown as Category[]);
    } catch (error) {
      console.error('Error fetching categories:', error);
      toast({
        title: "Error",
        description: "Failed to fetch categories",
        variant: "destructive",
      });
    } finally {
      setIsLoadingCategories(false);
    }
  };

  const handleSaveArticle = async () => {
    try {
      const now = new Date().toISOString().split('T')[0];
      const articleData = {
        ...articleForm,
        slug: articleForm.title.toLowerCase().replace(/\s+/g, '-').replace(/[^\w-]/g, ''),
        createdAt: editingItem ? editingItem.createdAt : now,
        updatedAt: now,
        views: editingItem ? editingItem.views : 0
      };

      if (editingItem) {
        await databases.updateDocument(
          appwriteConfig.databaseId,
          appwriteConfig.userGuideArtice,
          editingItem.$id,
          articleData
        );
        toast({ title: "Success", description: "Article updated successfully" });
      } else {
        await databases.createDocument(
          appwriteConfig.databaseId,
          appwriteConfig.userGuideArtice,
          'unique()',
          articleData
        );
        toast({ title: "Success", description: "Article created successfully" });
      }
      
      fetchArticles();
      setShowModal(false);
      resetForms();
    } catch (error) {
      console.error('Error saving article:', error);
      toast({
        title: "Error",
        description: "Failed to save article",
        variant: "destructive",
      });
    }
  };

  const handleSaveCategory = async () => {
    try {
      const now = new Date().toISOString().split('T')[0];
      const categoryData = {
        ...categoryForm,
        slug: categoryForm.title.toLowerCase().replace(/\s+/g, '-').replace(/[^\w-]/g, ''),
        createdAt: editingItem ? editingItem.createdAt : now,
        updatedAt: now,
        articleCount: editingItem ? editingItem.articleCount : 0
      };

      if (editingItem) {
        await databases.updateDocument(
          appwriteConfig.databaseId,
          appwriteConfig.userGuideCategories,
          editingItem.$id,
          categoryData
        );
        toast({ title: "Success", description: "Category updated successfully" });
      } else {
        await databases.createDocument(
          appwriteConfig.databaseId,
          appwriteConfig.userGuideCategories,
          'unique()',
          categoryData
        );
        toast({ title: "Success", description: "Category created successfully" });
      }
      
      fetchCategories();
      setShowModal(false);
      resetForms();
    } catch (error) {
      console.error('Error saving category:', error);
      toast({
        title: "Error",
        description: "Failed to save category",
        variant: "destructive",
      });
    }
  };

  const handleDeleteArticle = async (id: string) => {
    if (window.confirm('Are you sure you want to delete this article?')) {
      try {
        await databases.deleteDocument(appwriteConfig.databaseId, appwriteConfig.userGuideArtice, id);
        toast({ title: "Success", description: "Article deleted successfully" });
        fetchArticles();
      } catch (error) {
        console.error('Error deleting article:', error);
        toast({
          title: "Error",
          description: "Failed to delete article",
          variant: "destructive",
        });
      }
    }
  };

  const handleDeleteCategory = async (id: string) => {
    if (window.confirm('Are you sure you want to delete this category?')) {
      try {
        await databases.deleteDocument(appwriteConfig.databaseId, appwriteConfig.userGuideCategories, id);
        toast({ title: "Success", description: "Category deleted successfully" });
        fetchCategories();
      } catch (error) {
        console.error('Error deleting category:', error);
        toast({
          title: "Error",
          description: "Failed to delete category",
          variant: "destructive",
        });
      }
    }
  };

  const openModal = (type: 'article' | 'category', item: any = null) => {
    setModalType(type);
    setEditingItem(item);
    if (item) {
      if (type === 'article') {
        setArticleForm({
          title: item.title || '',
          content: item.content || '',
          excerpt: item.excerpt || '',
          categoryId: item.categoryId || '',
          readTime: item.readTime || 5,
          featured: item.featured || false,
          published: item.published || true,
          tags: item.tags || [],
          scheduledPublishDate: item.scheduledPublishDate || ''
        });
      } else {
        setCategoryForm({
          title: item.title || '',
          description: item.description || '',
          icon: item.icon || 'BookOpen',
          color: item.color || 'bg-blue-500',
          order: item.order || 1,
          published: item.published || true
        });
      }
    }
    setShowModal(true);
  };

  const resetForms = () => {
    setArticleForm({
      title: '',
      content: '',
      excerpt: '',
      categoryId: '',
      readTime: 5,
      featured: false,
      published: true,
      tags: [],
      scheduledPublishDate: ''
    });
    setCategoryForm({
      title: '',
      description: '',
      icon: 'BookOpen',
      color: 'bg-blue-500',
      order: 1,
      published: true
    });
    setEditingItem(null);
  };

  // Load data based on active tab
  useEffect(() => {
    switch (activeTab) {
      case 'products':
        fetchProducts();
        break;
      case 'scheduled':
        fetchScheduledLives();
        break;
      case 'banned':
        fetchBannedUsers();
        break;
      case 'userguide':
        fetchArticles();
        fetchCategories();
        break;
    }
  }, [activeTab]);

  // Reset search and filters when switching tabs
  useEffect(() => {
    if (activeTab === 'userguide') {
      setSearchQuery('');
      setSelectedCategory('all');
      setFilterStatus('all');
    }
  }, [activeTab]);

  const { mutateAsync: updatePost } = useUpdatePost();

  // Existing functions - unchanged
  const handleActivatePost = async (postId: string) => {
    try {
      const post = await getPostById(postId);
      if (post) {
        post.isActivated = true;
        post.postId = postId;
        const updatedPost = await updatePost(post as any);
        if (updatedPost) {
          toast({
            title: "Success",
            description: "Post activated successfully",
          });
        }
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to activate post",
        variant: "destructive",
      });
    }
  };

  const handleActivateProduct = async (productId: string) => {
    try {
      await databases.updateDocument(
        appwriteConfig.databaseId,
        appwriteConfig.products,
        productId,
        { isActivated: true }
      );
      toast({
        title: "Success",
        description: "Product activated successfully",
      });
      fetchProducts();
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to activate product",
        variant: "destructive",
      });
    }
  };

  const handleActivateScheduledLive = async (liveId: string) => {
    try {
      await databases.updateDocument(
        appwriteConfig.databaseId,
        appwriteConfig.scheduledLivesCollectionId,
        liveId,
        { isActivated: true }
      );
      toast({
        title: "Success",
        description: "Scheduled live activated successfully",
      });
      fetchScheduledLives();
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to activate scheduled live",
        variant: "destructive",
      });
    }
  };

  const handleRemoveBan = async (violationId: string) => {
    try {
      await databases.deleteDocument(
        appwriteConfig.databaseId,
        appwriteConfig.userViolationsCollectionId,
        violationId
      );
      toast({
        title: "Success",
        description: "User ban removed successfully",
      });
      fetchBannedUsers();
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to remove ban",
        variant: "destructive",
      });
    }
  };

  if (isErrorPosts || isErrorCreators) {
    return (
      <div className="flex flex-1">
        <div className="home-container">
          <p className="body-medium text-light-1">Something bad happened</p>
        </div>
      </div>
    );
  }

  // FIXED: Filter articles with proper search functionality
  const filteredArticles = articles.filter(article => {
    const matchesSearch = searchQuery === '' || 
      article.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      article.content.toLowerCase().includes(searchQuery.toLowerCase()) ||
      article.excerpt.toLowerCase().includes(searchQuery.toLowerCase());
      
    const matchesCategory = selectedCategory === 'all' || article.categoryId === selectedCategory;
    
    const matchesStatus = filterStatus === 'all' || 
                         (filterStatus === 'published' && article.published) ||
                         (filterStatus === 'draft' && !article.published && !article.scheduledPublishDate) ||
                         (filterStatus === 'scheduled' && article.scheduledPublishDate);
                         
    return matchesSearch && matchesCategory && matchesStatus;
  });

  // FIXED: Filter categories with search
  const filteredCategories = categories.filter(category => {
    if (guideActiveTab !== 'categories') return true;
    return searchQuery === '' || 
           category.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
           category.description.toLowerCase().includes(searchQuery.toLowerCase());
  });

  const renderUserGuideContent = () => {
    return (
      <div className="space-y-6 text-gray-400">
        <div className="flex justify-between items-center">
          <h2 className="h3-bold md:h2-bold text-left">User Guide Management</h2>
          <button
            onClick={() => openModal(guideActiveTab === 'articles' ? 'article' : 'category')}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add {guideActiveTab === 'articles' ? 'Article' : 'Category'}
          </button>
        </div>

        {/* Guide Sub-tabs */}
        <div className="flex border-b text-gray-700">
          <button
            onClick={() => setGuideActiveTab('articles')}
            className={`px-4 py-2 font-medium border-b-2 transition-colors ${
              guideActiveTab === 'articles'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            Articles ({articles.length})
          </button>
          <button
            onClick={() => setGuideActiveTab('categories')}
            className={`px-4 py-2 font-medium border-b-2 transition-colors ${
              guideActiveTab === 'categories'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            Categories ({categories.length})
          </button>
        </div>

        {/* Search and Filters */}
        <div className="flex gap-4 flex-wrap">
          <input
            type="text"
            placeholder={`Search ${guideActiveTab}...`}
            className="flex-1 min-w-64 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          
          {guideActiveTab === 'articles' && (
            <>
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
              >
                <option value="all">All Categories</option>
                {categories.map(category => (
                  <option key={category.$id} value={category.$id}>
                    {category.title}
                  </option>
                ))}
              </select>
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
              >
                <option value="all">All Status</option>
                <option value="published">Published</option>
                <option value="draft">Draft</option>
                <option value="scheduled">Scheduled</option>
              </select>
            </>
          )}
        </div>

        {/* Content */}
        {guideActiveTab === 'articles' ? (
          <div className="space-y-4">
            {isLoadingArticles ? (
              <Loader />
            ) : filteredArticles.length === 0 ? (
              <div className="text-center py-12">
                <BookOpen className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-light-2 mb-2">
                  {searchQuery ? 'No articles found matching your search' : 'No articles found'}
                </h3>
                <p className="text-gray-500 mb-6">
                  {searchQuery ? 'Try adjusting your search terms or filters.' : 'Get started by creating your first article.'}
                </p>
                {!searchQuery && (
                  <button
                    onClick={() => openModal('article')}
                    className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    Create Article
                  </button>
                )}
              </div>
            ) : (
              filteredArticles.map(article => (
                <div key={article.$id} className="border border-gray-300 text-gray-700 rounded-lg p-4">
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="flex items-center space-x-2 mb-2">
                        <h3 className="text-lg font-semibold">{article.title}</h3>
                        {article.featured && <Star className="w-4 h-4 text-yellow-500" />}
                        <span className={`px-2 py-1 text-xs rounded-full ${
                          article.published 
                            ? 'bg-green-100 text-green-800'
                            : article.scheduledPublishDate
                            ? 'bg-yellow-100 text-yellow-800'
                            : 'bg-gray-100 text-gray-800'
                        }`}>
                          {article.published 
                            ? 'Published'
                            : article.scheduledPublishDate
                            ? 'Scheduled'
                            : 'Draft'
                          }
                        </span>
                      </div>
                      <p className="text-gray-600 mb-2">{article.excerpt}</p>
                      <div className="flex items-center space-x-4 text-sm text-gray-500 mb-2">
                        <span><Clock className="w-4 h-4 inline mr-1" />{article.readTime} min read</span>
                        <span><Eye className="w-4 h-4 inline mr-1" />{article.views} views</span>
                        <span>Updated: {article.updatedAt}</span>
                      </div>
                      {article.tags && article.tags.length > 0 && (
                        <div className="flex flex-wrap gap-1">
                          {article.tags.map((tag, index) => (
                            <span key={`${tag}-${index}`} className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded">
                              {tag}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                    <div className="flex items-center space-x-2 ml-4">
                      <button
                        onClick={() => openModal('article', article)}
                        className="p-2 text-gray-400 hover:text-blue-600 transition-colors"
                      >
                        <Edit3 className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDeleteArticle(article.$id)}
                        className="p-2 text-gray-400 hover:text-rose-600 transition-colors"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        ) : (
          <div className="space-y-4">
            {isLoadingCategories ? (
              <Loader />
            ) : filteredCategories.length === 0 ? (
              <div className="text-center py-12">
                <Settings className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-light-2 mb-2">
                  {searchQuery ? 'No categories found matching your search' : 'No categories found'}
                </h3>
                <p className="text-gray-500 mb-6">
                  {searchQuery ? 'Try adjusting your search terms.' : 'Create your first category to organize articles.'}
                </p>
                {!searchQuery && (
                  <button
                    onClick={() => openModal('category')}
                    className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    Create Category
                  </button>
                )}
              </div>
            ) : (
              filteredCategories.map(category => (
                <div key={category.$id} className="border border-gray-300 rounded-lg p-4">
                  <div className="flex justify-between items-start">
                    <div className="flex items-start space-x-4">
                      <div className={`p-3 rounded-lg ${category.color} bg-opacity-10`}>
                        <BookOpen className="w-6 h-6" />
                      </div>
                      <div>
                        <div className="flex items-center space-x-2 mb-2">
                          <h3 className="text-lg font-semibold">{category.title}</h3>
                          <span className={`px-2 py-1 text-xs rounded-full ${
                            category.published 
                              ? 'bg-green-100 text-green-800'
                              : 'bg-gray-100 text-gray-800'
                          }`}>
                            {category.published ? 'Published' : 'Draft'}
                          </span>
                        </div>
                        <p className="text-gray-600 mb-2">{category.description}</p>
                        <div className="flex items-center space-x-4 text-sm text-gray-500">
                          <span>{category.articleCount} articles</span>
                          <span>Order: {category.order}</span>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => openModal('category', category)}
                        className="p-2 text-gray-400 hover:text-blue-600 transition-colors"
                      >
                        <Edit3 className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDeleteCategory(category.$id)}
                        className="p-2 text-gray-400 hover:text-rose-600 transition-colors"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        )}
      </div>
    );
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 'userguide':
        return renderUserGuideContent();
        
      case 'posts':
        return (
          <div className="home-posts">
            <h2 className="h3-bold md:h2-bold text-left w-full">Post Moderation</h2>
            {isPostLoading && !data ? (
              <Loader />
            ) : (
              <ul className="flex flex-col flex-1 gap-9 w-full">
                {data?.pages?.map((page, pageIndex) =>
                  page.documents
                    .filter((post: Models.Document) => !post.isActivated)
                    .map((post: Models.Document, index) => {
                      const isLastPost =
                        pageIndex === data.pages.length - 1 &&
                        index === page.documents.length - 1 &&
                        !isFetchingNextPage;
                      return (
                        <li
                          key={post.$id}
                          ref={isLastPost ? lastPostRef : null}
                          className="flex flex-col items-center w-full border rounded-lg p-4"
                        >
                          <PostCard post={post} showDealBadge={true} />
                          <button
                            onClick={() => handleActivatePost(post.$id)}
                            className="mt-4 px-6 py-2 bg-green-500 text-white rounded hover:bg-green-600 transition-colors"
                          >
                            Activate Post
                          </button>
                        </li>
                      );
                    })
                )}
              </ul>
            )}
            {isFetchingNextPage && <Loader />}
          </div>
        );

      case 'products':
        return (
          <div className="home-posts">
            <h2 className="h3-bold md:h2-bold text-left w-full">Product Moderation</h2>
            {isLoadingProducts ? (
              <Loader />
            ) : (
              <ul className="flex flex-col flex-1 gap-4 w-full">
                {products.map((product) => (
                  <li key={product.$id} className="border rounded-lg p-4">
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold">{product.title}</h3>
                        <p className="text-gray-600 mt-1">Price: ₹{product.price}</p>
                        <p className="text-sm text-gray-500 mt-2">{product.description}</p>
                        <div className="flex gap-2 mt-3">
                          {product.images.slice(0, 3).map((image, idx) => (
                            <img
                              key={idx}
                              src={image}
                              alt={`Product ${idx + 1}`}
                              className="w-16 h-16 object-cover rounded"
                            />
                          ))}
                        </div>
                      </div>
                      <button
                        onClick={() => handleActivateProduct(product.$id)}
                        className="ml-4 px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600 transition-colors"
                      >
                        Activate Product
                      </button>
                    </div>
                  </li>
                ))}
                {products.length === 0 && (
                  <p className="text-center text-gray-500">No products pending approval</p>
                )}
              </ul>
            )}
          </div>
        );

      case 'scheduled':
        return (
          <div className="home-posts">
            <h2 className="h3-bold md:h2-bold text-left w-full">Scheduled Live Moderation</h2>
            {isLoadingScheduled ? (
              <Loader />
            ) : (
              <ul className="flex flex-col flex-1 gap-4 w-full">
                {scheduledLives.map((live) => (
                  <li key={live.$id} className="border rounded-lg p-4">
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold">{live.title}</h3>
                        <p className="text-gray-600 mt-1">
                          Scheduled: {new Date(live.scheduledAt).toLocaleString()}
                        </p>
                        <p className="text-sm text-gray-500 mt-1">
                          Products: {live.products.length} items
                        </p>
                        {live.discountPercent && (
                          <p className="text-sm text-green-600 mt-1">
                            Discount: {live.discountPercent}% 
                            {live.discountMaxUsers && ` (Max ${live.discountMaxUsers} users)`}
                          </p>
                        )}
                        <p className="text-xs text-gray-400 mt-2">Status: {live.status}</p>
                      </div>
                      <button
                        onClick={() => handleActivateScheduledLive(live.$id)}
                        className="ml-4 px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600 transition-colors"
                      >
                        Activate Live
                      </button>
                    </div>
                  </li>
                ))}
                {scheduledLives.length === 0 && (
                  <p className="text-center text-gray-500">No scheduled lives pending approval</p>
                )}
              </ul>
            )}
          </div>
        );

      case 'banned':
        return (
          <div className="home-posts">
            <h2 className="h3-bold md:h2-bold text-left w-full">Banned Users</h2>
            {isLoadingBanned ? (
              <Loader />
            ) : (
              <ul className="flex flex-col flex-1 gap-4 w-full">
                {bannedUsers.map((violation) => (
                  <li key={violation.$id} className="border rounded-lg p-4 bg-rose-50">
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold text-rose-800">
                          {violation.username}
                        </h3>
                        <p className="text-gray-600 mt-1">{violation.userEmailOrPhone}</p>
                        <p className="text-sm text-gray-700 mt-2">
                          <strong>Violation Type:</strong> {violation.lastViolationType}
                        </p>
                        <p className="text-sm text-gray-700 mt-1">
                          <strong>Count:</strong> {violation.violationCount}
                        </p>
                        <p className="text-sm text-gray-700 mt-1">
                          <strong>Stage:</strong> {violation.violationStage}
                        </p>
                        <div className="mt-3 p-3 bg-gray-100 rounded">
                          <p className="text-sm text-gray-800">
                            <strong>Message:</strong> "{violation.messageText}"
                          </p>
                        </div>
                        {violation.flaggedUntil && (
                          <p className="text-xs text-rose-600 mt-2">
                            Flagged until: {new Date(violation.flaggedUntil).toLocaleString()}
                          </p>
                        )}
                      </div>
                      <button
                        onClick={() => handleRemoveBan(violation.$id)}
                        className="ml-4 px-4 py-2 bg-rose-500 text-white rounded hover:bg-rose-600 transition-colors"
                      >
                        Remove Ban
                      </button>
                    </div>
                  </li>
                ))}
                {bannedUsers.length === 0 && (
                  <p className="text-center text-gray-500">No banned users</p>
                )}
              </ul>
            )}
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="flex flex-1">
      <div className="home-container">
        {/* Navigation Tabs */}
        <div className="flex border-b mb-6">
          <button
            onClick={() => setActiveTab('posts')}
            className={`px-6 py-3 font-medium border-b-2 transition-colors ${
              activeTab === 'posts'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            Create Posts
          </button>
          <button
            onClick={() => setActiveTab('products')}
            className={`px-6 py-3 font-medium border-b-2 transition-colors ${
              activeTab === 'products'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            Live Products
          </button>
          <button
            onClick={() => setActiveTab('scheduled')}
            className={`px-6 py-3 font-medium border-b-2 transition-colors ${
              activeTab === 'scheduled'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            Scheduled Live
          </button>
          <button
            onClick={() => setActiveTab('banned')}
            className={`px-6 py-3 font-medium border-b-2 transition-colors ${
              activeTab === 'banned'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            Banned Users
          </button>
          {/* NEW: User Guide Tab */}
          <button
            onClick={() => setActiveTab('userguide')}
            className={`px-6 py-3 font-medium border-b-2 transition-colors ${
              activeTab === 'userguide'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <BookOpen className="w-4 h-4 inline mr-2" />
            User Guide
          </button>
        </div>

        {/* Tab Content */}
        {renderTabContent()}
      </div>

      {/* Right Sidebar - Only show for posts tab */}
      {activeTab === 'posts' && (
        <div className="home-creators">
          <h3 className="h3-bold text-light-1">User Suggestions</h3>
          {isUserLoading && !creators ? (
            <Loader />
          ) : (
            <ul className="grid 2xl:grid-cols-2 gap-6">
              {creators?.documents.map((creator) => (
                <li key={creator?.$id}>
                  <UserCard user={creator} showTrustScore={true} />
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {/* NEW: Modal for User Guide */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 text-gray-700 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">
                {editingItem ? 'Edit' : 'Create'} {modalType === 'article' ? 'Article' : 'Category'}
              </h3>
              <button
                onClick={() => setShowModal(false)}
                className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6 text-gray-700">
              {modalType === 'article' ? (
                <div className="space-y-4">
                  <input
                    type="text"
                    placeholder="Article title"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                    value={articleForm.title}
                    onChange={(e) => setArticleForm({...articleForm, title: e.target.value})}
                  />
                  <input
                    type="text"
                    placeholder="Brief excerpt"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                    value={articleForm.excerpt}
                    onChange={(e) => setArticleForm({...articleForm, excerpt: e.target.value})}
                  />
                  <textarea
                    rows={6}
                    placeholder="Article content"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                    value={articleForm.content}
                    onChange={(e) => setArticleForm({...articleForm, content: e.target.value})}
                  />
                  <div className="grid grid-cols-2 gap-4">
                    <select
                      className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                      value={articleForm.categoryId}
                      onChange={(e) => setArticleForm({...articleForm, categoryId: e.target.value})}
                    >
                      <option value="">Select category</option>
                      {categories.map(category => (
                        <option key={category.$id} value={category.$id}>
                          {category.title}
                        </option>
                      ))}
                    </select>
                    <input
                      type="number"
                      placeholder="Read time (min)"
                      className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                      value={articleForm.readTime}
                      onChange={(e) => setArticleForm({...articleForm, readTime: parseInt(e.target.value) || 5})}
                    />
                  </div>
                  <input
                    type="text"
                    placeholder="Tags (comma separated)"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                    value={articleForm.tags.join(', ')}
                    onChange={(e) => setArticleForm({...articleForm, tags: e.target.value.split(',').map(tag => tag.trim()).filter(tag => tag)})}
                  />
                  <input
                    type="date"
                    placeholder="Scheduled publish date (optional)"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                    value={articleForm.scheduledPublishDate}
                    onChange={(e) => setArticleForm({...articleForm, scheduledPublishDate: e.target.value})}
                  />
                  <div className="flex items-center space-x-4">
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={articleForm.featured}
                        onChange={(e) => setArticleForm({...articleForm, featured: e.target.checked})}
                        className="mr-2"
                      />
                      Featured
                    </label>
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={articleForm.published}
                        onChange={(e) => setArticleForm({...articleForm, published: e.target.checked})}
                        className="mr-2"
                      />
                      Published
                    </label>
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  <input
                    type="text"
                    placeholder="Category title"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                    value={categoryForm.title}
                    onChange={(e) => setCategoryForm({...categoryForm, title: e.target.value})}
                  />
                  <textarea
                    rows={3}
                    placeholder="Category description"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                    value={categoryForm.description}
                    onChange={(e) => setCategoryForm({...categoryForm, description: e.target.value})}
                  />
                  <div className="grid grid-cols-2 gap-4">
                    <select
                      className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                      value={categoryForm.color}
                      onChange={(e) => setCategoryForm({...categoryForm, color: e.target.value})}
                    >
                      <option value="bg-blue-500">Blue</option>
                      <option value="bg-green-500">Green</option>
                      <option value="bg-purple-500">Purple</option>
                      <option value="bg-orange-500">Orange</option>
                      <option value="bg-rose-500">Red</option>
                      <option value="bg-indigo-500">Indigo</option>
                      <option value="bg-pink-500">Pink</option>
                      <option value="bg-yellow-500">Yellow</option>
                    </select>
                    <input
                      type="number"
                      placeholder="Order"
                      min="1"
                      className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                      value={categoryForm.order}
                      onChange={(e) => setCategoryForm({...categoryForm, order: parseInt(e.target.value) || 1})}
                    />
                  </div>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={categoryForm.published}
                      onChange={(e) => setCategoryForm({...categoryForm, published: e.target.checked})}
                      className="mr-2"
                    />
                    Published
                  </label>
                </div>
              )}
            </div>

            <div className="flex items-center justify-end space-x-4 p-6 border-t border-gray-200">
              <button
                onClick={() => setShowModal(false)}
                className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={modalType === 'article' ? handleSaveArticle : handleSaveCategory}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center"
                disabled={!articleForm.title.trim() && !categoryForm.title.trim()}
              >
                <Save className="w-4 h-4 mr-2" />
                {editingItem ? 'Update' : 'Create'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Admin;